// src/apis/partnerBulk.ts
//
// Partner bulk pricing APIs
// - Bulk upload: upload file, list histories, delete history.
// - Bulk upload detail: list details, count details, confirm upload.
// - All functions return server payloads as-is (PageResponse<T>) except count helpers,
//   which normalize to a safe number for UI usage.
//
// Notes
// - Count endpoints in this API return the count as `data.data[0]`.
//   This module normalizes missing/invalid values to 0 to keep pagination stable.
// - `onlyFailed` is sent only when it is true (keeps query params clean).

import API from './API';
import type { PageParams, PageResponse } from '@models/api';
import type {
  PartnerProductPriceBulkUpload,
  PartnerProductPriceBulkUploadDetailResponse,
} from '../models/partnerBulk';

/* ------------------------------------------------------------------ */
/* Upload                                                              */
/* ------------------------------------------------------------------ */

/**
 * Upload a partner pricing bulk file.
 * The backend expects multipart/form-data with key "file".
 */
export const uploadPriceFile = async (partnerId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const { data } = await API.post<PageResponse<any>>(
    `/v1/partners/${partnerId}/price-bulk/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );

  return data;
};

/**
 * Get total count of upload histories.
 * API convention: count is stored in `data.data[0]`.
 */
export const getPriceUploadHistoriesCount = async (partnerId: number) => {
  const { data } = await API.get<PageResponse<number>>(
    `/v1/partners/${partnerId}/price-bulk/upload/count`,
  );

  const count = Number(data?.data?.[0]);
  return Number.isFinite(count) ? count : 0;
};

/**
 * Get paginated upload histories.
 */
export const getPriceUploadHistories = async (partnerId: number, params: PageParams) => {
  const { data } = await API.get<PageResponse<PartnerProductPriceBulkUpload>>(
    `/v1/partners/${partnerId}/price-bulk/upload`,
    { params },
  );

  return data;
};

/**
 * Get a single upload history by id.
 */
export const getPriceUploadHistory = async (partnerId: number, uploadId: number) => {
  const { data } = await API.get<PageResponse<PartnerProductPriceBulkUpload>>(
    `/v1/partners/${partnerId}/price-bulk/upload/${uploadId}`,
  );

  return data;
};

/**
 * Delete an upload history.
 */
export const deletePriceUploadHistory = async (partnerId: number, uploadId: number) => {
  const { data } = await API.delete(
    `/v1/partners/${partnerId}/price-bulk/upload/${uploadId}`,
  );

  return data;
};

/* ------------------------------------------------------------------ */
/* Upload detail                                                       */
/* ------------------------------------------------------------------ */

export type GetPriceUploadHistoryDetailsParams = {
  onlyFailed?: boolean;
};

/**
 * Get total count of upload detail rows.
 * API convention: count is stored in `data.data[0]`.
 * - `onlyFailed` is included only when true.
 */
export const getPriceUploadHistoryDetailsCount = async (
  partnerId: number,
  uploadId: number,
  params?: GetPriceUploadHistoryDetailsParams,
) => {
  const onlyFailed = Boolean(params?.onlyFailed);

  const { data } = await API.get<PageResponse<number>>(
    `/v1/partners/${partnerId}/price-bulk/upload/${uploadId}/details/count`,
    { params: onlyFailed ? { onlyFailed: true } : {} },
  );

  const count = Number(data?.data?.[0]);
  return Number.isFinite(count) ? count : 0;
};

/**
 * Get paginated upload detail rows.
 */
export const getPriceUploadHistoryDetails = async (
  partnerId: number,
  uploadId: number,
  params: PageParams & GetPriceUploadHistoryDetailsParams,
) => {
  const { data } = await API.get<PageResponse<PartnerProductPriceBulkUploadDetailResponse>>(
    `/v1/partners/${partnerId}/price-bulk/upload/${uploadId}/details`,
    { params },
  );

  return data;
};

/**
 * Confirm (apply) an uploaded pricing file.
 */
export const confirmPriceUpload = async (partnerId: number, uploadId: number) => {
  const { data } = await API.post(
    `/v1/partners/${partnerId}/price-bulk/upload/${uploadId}/confirm`,
  );

  return data;
};
